import { useState, useEffect, useRef } from 'preact/hooks';
import { appendDashboardFiltersToSearchParams, dashboardFilters, dateRangePreset, selectedProperty, startDate, endDate } from './state';
import VisitsChart from './VisitsChart';
import { api } from './api';
import { getEffectiveRealtimeEndDate } from './dateRange';
import { subscribeToVisitsRealtime } from './realtime';
import type { RealtimeConnectionStatus } from './realtime';

type VisitsProps = {
    liveRefreshTick?: number;
};

const Visits = ({ liveRefreshTick = 0 }: VisitsProps) => {
    const [visitsData, setVisitsData] = useState<{ day: string; visits: number }[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalVisits, setTotalVisits] = useState(0);
    const [uniqueVisitors, setUniqueVisitors] = useState(0);
    const [pageViews, setPageViews] = useState(0);
    const [viewsPerVisit, setViewsPerVisit] = useState(0);
    const [bounceRate, setBounceRate] = useState(0);
    const [avgDuration, setAvgDuration] = useState(0);
    const [realtimeStatus, setRealtimeStatus] = useState<RealtimeConnectionStatus>('disconnected');
    const refreshVisitsRef = useRef<(showLoading?: boolean) => void>(() => {});

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        let cancelled = false;

        const loadVisits = async (showLoading = true) => {
            if (showLoading) {
                setLoading(true);
            }

            try {
                const effectiveEndDate = getEffectiveRealtimeEndDate(endDate.value);
                const params = new URLSearchParams({
                    property: selectedProperty.value,
                    start: startDate.value,
                    end: effectiveEndDate,
                });
                appendDashboardFiltersToSearchParams(params);
                const result = await api(`/visits?${params.toString()}`);

                if (cancelled) {
                    return;
                }

                setVisitsData(result.visits);
                setTotalVisits(result.totalVisits);
                setUniqueVisitors(result.uniqueVisitors);
                setPageViews(result.pageViews);
                setViewsPerVisit(result.viewsPerVisit);
                setBounceRate(result.bounceRate);
                setAvgDuration(result.avgDuration);
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching visits data:', error);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        refreshVisitsRef.current = (showLoading = false) => {
            void loadVisits(showLoading);
        };

        let refreshTimer: number | null = null;
        const scheduleRefresh = () => {
            if (refreshTimer !== null) {
                return;
            }

            refreshTimer = window.setTimeout(() => {
                refreshTimer = null;
                void loadVisits(false);
            }, 500);
        };

        void loadVisits(true);
        const unsubscribe = subscribeToVisitsRealtime(
            selectedProperty.value,
            scheduleRefresh,
            setRealtimeStatus
        );

        return () => {
            cancelled = true;
            if (refreshTimer !== null) {
                window.clearTimeout(refreshTimer);
            }
            setRealtimeStatus('disconnected');
            unsubscribe();
        };
    }, [selectedProperty.value, startDate.value, endDate.value, JSON.stringify(dashboardFilters.value)]);

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        if (dateRangePreset.value !== 'live') {
            return;
        }

        if (realtimeStatus === 'connected') {
            return;
        }

        const pollId = window.setInterval(() => {
            refreshVisitsRef.current(false);
        }, 5000);

        return () => {
            window.clearInterval(pollId);
        };
    }, [realtimeStatus, selectedProperty.value, startDate.value, endDate.value, dateRangePreset.value, JSON.stringify(dashboardFilters.value)]);

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        if (dateRangePreset.value !== 'live' || liveRefreshTick === 0) {
            return;
        }

        refreshVisitsRef.current(false);
    }, [liveRefreshTick, selectedProperty.value, startDate.value, endDate.value, JSON.stringify(dashboardFilters.value)]);

    const formatDuration = (seconds: number) => {
        if (!seconds) return '0s';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString();
    };

    const realtimeLabel = realtimeStatus === 'connected' ? 'Live' : 'Offline';

    return (
        <div>

            <div className="chart-container">
                <div className="card-header">
                    <div>
                        <h2 className="card-title">
                            Visite {loading ? ' Caricamento...' : ` dal ${formatDate(startDate.value)} al ${formatDate(endDate.value)}`}
                        </h2>
                        <p className="section-note">KPI calcolati sulle sessioni con consenso.</p>
                    </div>
                    <div className={`realtime-indicator realtime-indicator-${realtimeStatus === 'connected' ? 'connected' : 'disconnected'}`}>
                        <span className="realtime-indicator-dot" />
                        <span>{realtimeLabel}</span>
                    </div>
                </div>

                <div className="stats-row">
                    <div className="stat-item">
                        <div className="stat-label">Visitatori unici</div>
                        <div className="stat-value">{uniqueVisitors.toLocaleString()}</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">Sessioni</div>
                        <div className="stat-value">{totalVisits.toLocaleString()}</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">Visualizzazioni</div>
                        <div className="stat-value">{pageViews.toLocaleString()}</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">Pageview per sessione</div>
                        <div className="stat-value">{viewsPerVisit.toFixed(2)}</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">Frequenza di rimbalzo</div>
                        <div className="stat-value">{bounceRate.toFixed(1)}%</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-label">Durata media visita</div>
                        <div className="stat-value">{formatDuration(avgDuration)}</div>
                    </div>
                </div>

                <VisitsChart data={visitsData} />
            </div>
        </div>
    );
};

export default Visits;
